import { ComponentProps, CSSProperties, forwardRef } from 'react';
import ReactSelect, { ClearIndicatorProps } from 'react-select';

import { CSSObject } from '@emotion/serialize';

import { ClearIcon } from '../Icons';

export type SelectProps = ComponentProps<typeof ReactSelect> & {
  getOptionLabel?(value: any): string;
  controlStyles?: any;
  singleValueStyles?: any;
  dropDownIndicatorStyles?: any;
  labelAttribute?: string;
  valueAttribute?: string;
};

const ClearIndicatorStyles = (
  base: CSSObject,
  state: ClearIndicatorProps<unknown>,
): CSSObject => ({
  ...base,
  cursor: 'pointer',
  color: state.isFocused ? 'rgb(102,102,102)' : 'hsl(0, 0%, 80%)',
});

export interface DropdownOption {
  id: string;
  name: string;
}

const Select: React.ForwardRefRenderFunction<any, SelectProps> = (
  {
    labelAttribute = 'name',
    valueAttribute = 'id',
    placeholder = 'Select',
    isClearable = true,
    isSearchable = true,
    controlStyles = {},
    singleValueStyles = {},
    dropDownIndicatorStyles = {},
    getOptionLabel,
    ...props
  },
  ref,
) => {
  return (
    <ReactSelect
      ref={ref}
      instanceId={'simplr'}
      styles={{
        placeholder: base => ({
          ...base,
          color: '#9ca3af',
        }),
        menu: provided => ({ ...provided, zIndex: 1000 }),
        singleValue: base => {
          return {
            ...base,
            color: '#045F8C',
            ...singleValueStyles,
          };
        },
        dropdownIndicator: base => {
          return {
            ...base,
            ...dropDownIndicatorStyles,
          };
        },
        control: (defaultControlProps: any) => {
          return {
            ...defaultControlProps,
            borderColor: '#afe2ff',
            minHeight: '38px',
            paddingLeft: '3px',
            borderWidth: '1px',
            borderRadius: 0,
            ...controlStyles,
          };
        },
        clearIndicator: ClearIndicatorStyles,
      }}
      components={{
        IndicatorSeparator: () => null,
        ClearIndicator: props => {
          const {
            getStyles,
            innerProps: { ref, ...restInnerProps },
          } = props;
          return (
            <div
              {...restInnerProps}
              ref={ref}
              style={getStyles('clearIndicator', props) as CSSProperties}
            >
              <div
                style={{
                  cursor: 'pointer',
                }}
              >
                <ClearIcon />
              </div>
            </div>
          );
        },
      }}
      placeholder={placeholder}
      isClearable={isClearable}
      isSearchable={isSearchable}
      getOptionLabel={getOptionLabel || ((value: any) => value[labelAttribute])}
      getOptionValue={(value: any) => value[valueAttribute]}
      {...props}
    />
  );
};

export default forwardRef(Select);
